<ng-template #readOnlyBadge>
  <span class="read-only-badge">閲覧のみ</span>
</ng-template>

<section
  #feedbackShell
  class="card feedback-shell"
  aria-labelledby="feedback-heading"
>
  @if (isLoading || feedbackSheets.length === 0) {
    <div class="section-header">
      <h2 id="feedback-heading">個別支援フィードバックシート</h2>
    </div>
  } @else {
    <div #feedbackStickyHead class="feedback-sticky-head">
      <h2 id="feedback-heading">個別支援フィードバックシート</h2>
      <div class="feedback-toolbar">
        <div
          class="sheet-tabs"
          role="tablist"
          aria-label="フィードバックシート"
        >
          @for (sheet of feedbackSheets; track sheet.id) {
            <button
              #feedbackSheetTab
              class="sheet-tab"
              [class.active]="selectedSheetId === sheet.id"
              type="button"
              role="tab"
              [id]="'feedback-sheet-tab-' + sheet.id"
              [attr.aria-controls]="'feedback-sheet-panel-' + sheet.id"
              [attr.aria-selected]="selectedSheetId === sheet.id"
              [tabindex]="selectedSheetId === sheet.id ? 0 : -1"
              [disabled]="isChangingAssessments"
              (click)="selectFeedbackSheet(sheet.id)"
              (keydown)="moveFeedbackSheetTab($event, sheet.id)"
            >
              第{{ sheet.sheetNumber }}回
              <span
                class="status-dot"
                [class.generating]="sheet.aiDraftStatus === 'PENDING'"
                [class.failed]="sheet.aiDraftStatus === 'FAILED'"
                [title]="
                  sheet.aiDraftStatus === 'PENDING'
                    ? 'AI草案を生成中'
                    : sheet.aiDraftStatus === 'FAILED'
                      ? 'AI草案の生成に失敗'
                      : '作成済み'
                "
              ></span>
            </button>
          }
        </div>
        @if (canCreateSheet) {
          <button
            class="btn btn-primary btn-sm create-feedback-sheet"
            type="button"
            [disabled]="isChangingAssessments"
            (click)="openNewFeedbackSheetDialog()"
          >
            <mat-icon aria-hidden="true">add</mat-icon>
            <span>新規作成</span>
          </button>
        }
      </div>
    </div>
  }
  <p class="section-description">
    面談前に草案を確認し、面談中に合意したネクストアクションを記録します。
    @if (viewer.canManage) {
      主任・運用責任者はすべての欄を編集できます。
    } @else {
      オペレーター記入欄（相談事項・自身の課題感・自身が思う良かった点／改善点・前回ネクストアクションの結果）はこの画面で入力できます。
      <span id="feedback-manager-read-only"
        >主任記入欄と面談中に記入する欄は閲覧のみです。</span
      >
    }
  </p>

  @if (isLoading || (isSheetLoading && selectedSheetId == null)) {
    <div class="sheet-loading" aria-live="polite">
      <mat-spinner diameter="30" aria-hidden="true" />
      <span>フィードバックシートを読み込み中です。</span>
    </div>
  } @else if (feedbackErrorMessage && feedbackSheets.length === 0) {
    <div class="feedback-load-error" role="alert">
      <p>{{ feedbackErrorMessage }}</p>
      <button type="button" class="btn btn-secondary btn-sm" (click)="retry()">
        再試行
      </button>
    </div>
  } @else if (feedbackSheets.length === 0) {
    <div
      class="feedback-empty-state"
      role="region"
      aria-labelledby="feedback-empty-heading"
      aria-describedby="feedback-empty-description"
    >
      <div class="feedback-empty-inner">
        <mat-icon class="feedback-empty-icon" aria-hidden="true">
          description
        </mat-icon>
        <h3 id="feedback-empty-heading">シートはまだありません</h3>
        <p id="feedback-empty-description">
          対象アセスメントを選択して最初のフィードバックシートを作成しましょう。
        </p>
        @if (canCreateSheet) {
          <button
            class="btn btn-primary btn-sm create-feedback-sheet"
            type="button"
            [disabled]="isChangingAssessments"
            (click)="openNewFeedbackSheetDialog()"
          >
            <mat-icon aria-hidden="true">add</mat-icon>
            <span>新規作成</span>
          </button>
        }
      </div>
    </div>
  } @else {
    @if (feedbackErrorMessage) {
      <p class="feedback-operation-error" role="alert">
        {{ feedbackErrorMessage }}
      </p>
    }

    @if (isSheetLoading) {
      <div
        class="sheet-loading"
        [class.sheet-detail-loading]="shouldPreserveSheetLoadingHeight"
        aria-live="polite"
      >
        <mat-spinner diameter="30" aria-hidden="true" />
        <span>フィードバックシートを読み込み中です。</span>
      </div>
    } @else if (selectedSheet != null) {
      <div
        #sheetPanel
        class="sheet-panel active"
        role="tabpanel"
        [id]="'feedback-sheet-panel-' + selectedSheet.id"
        [attr.aria-labelledby]="'feedback-sheet-tab-' + selectedSheet.id"
      >
        <div class="sheet-content" [formGroup]="sheetForm">
          <div class="sheet-answer-status">
            <span>
              <span role="status">
                <strong>
                  {{
                    isResponseAnswered
                      ? '記入完了（引き続き編集できます）'
                      : 'オペレーター記入欄は未記入です'
                  }}
                </strong>
              </span>
              <br />
              @if (isOperatorSelf && isResponseAnswered) {
                面談前までは記入内容を編集できます。編集内容は自動で保存されます。
              } @else if (isOperatorSelf) {
                オペレーター記入欄（相談事項・自身の課題感・良かった点／改善点・前回ネクストアクションの結果）を入力したら「記入完了」を押してください。一度記入完了にすると未記入には戻せません。完了後も面談前まで編集できます。
              } @else {
                オペレーター本人が記入完了すると表示が切り替わります。
              }
            </span>
            <div class="sheet-actions">
              @if (isOperatorSelf && !isResponseAnswered) {
                <button
                  class="btn btn-primary btn-sm complete-feedback-response"
                  type="button"
                  [disabled]="isCompletingResponse || isChangingAssessments"
                  (click)="completeFeedbackSheetResponse()"
                >
                  {{ isCompletingResponse ? '記録中...' : '記入完了' }}
                </button>
              }
              @if (canChangeAssessments) {
                <button
                  class="btn btn-secondary btn-sm change-feedback-targets"
                  type="button"
                  [disabled]="
                    isChangingAssessments || isDeletingSheet || isSheetLoading
                  "
                  (click)="openChangeFeedbackSheetDialog()"
                >
                  対象アセスメントを変更
                </button>
              }
              @if (canDeleteSheet) {
                <button
                  class="btn btn-danger btn-sm delete-feedback-sheet"
                  type="button"
                  [disabled]="isDeletingSheet || isChangingAssessments"
                  (click)="deleteSelectedFeedbackSheet()"
                >
                  {{ isDeletingSheet ? '削除中...' : 'シートを削除' }}
                </button>
              }
            </div>
          </div>

          @if (isAiDraftFailed) {
            <div class="ai-draft-notice failed" role="status">
              <mat-icon aria-hidden="true">error_outline</mat-icon>
              <span>
                <strong>AI 草案の生成に失敗しました。</strong>
                @if (viewer.canManage) {
                  各項目は通常どおり手動で記入できます。
                } @else {
                  主任・運用責任者が手動で記入するまで、主任記入欄は空のまま表示されます。
                }
              </span>
            </div>
          } @else if (isAiDraftPollingTimedOut) {
            <div class="ai-draft-notice timed-out" role="status">
              <mat-icon aria-hidden="true">schedule</mat-icon>
              <span>
                AI
                草案の生成に時間がかかっています。最新の状態を確認してください。
                @if (viewer.canManage) {
                  作成中のまま 30
                  分以上経過した場合は、再読み込みすると「対象アセスメントを変更」から草案を作り直せます。
                }
                @if (aiDraftReloadErrorMessage) {
                  <small role="alert">{{ aiDraftReloadErrorMessage }}</small>
                }
              </span>
              <button
                class="btn btn-secondary btn-sm reload-ai-draft"
                type="button"
                [disabled]="isAiDraftReloading || isChangingAssessments"
                (click)="reloadAiDraft()"
              >
                {{ isAiDraftReloading ? '再読み込み中...' : '再読み込み' }}
              </button>
            </div>
          }

          @if (showAiReferencedKnowledges) {
            <div class="ai-referenced-knowledges">
              <span
                class="knowledge-label"
                id="feedback-ai-referenced-knowledges-label"
                >AI 草案の参照ナレッジ</span
              >
              <ul
                class="knowledge-chips"
                role="list"
                aria-labelledby="feedback-ai-referenced-knowledges-label"
              >
                @for (
                  knowledge of selectedSheet.aiReferencedKnowledges;
                  track knowledge.id
                ) {
                  <li class="knowledge-chip">
                    {{ knowledge.name
                    }}{{ knowledge.active ? '' : '（削除済み）' }}
                  </li>
                }
              </ul>
            </div>
          }

          <div class="feedback-save-state" aria-live="polite">
            @if (feedbackSaveStatus === 'saving') {
              <span class="saving">保存中...</span>
            } @else if (feedbackSaveStatus === 'saved') {
              <span class="saved">保存しました</span>
            }
            @if (feedbackSaveErrorMessage) {
              <span class="save-failed" role="alert">
                {{ feedbackSaveErrorMessage }}
              </span>
            }
          </div>

          @if (
            selectedSheet.previousSheet != null &&
            previousNextActionRows.length > 0
          ) {
            <section class="form-section previous-actions-section">
              <div class="form-section-title">
                <span class="section-number">0</span>
                <h3>前回ネクストアクションの振り返り</h3>
              </div>
              <p class="previous-actions-note">
                前回までのシートで継続中のネクストアクションと、このシート作成後に完了した項目を表示します。「実践できた」の回答、または主任・運用責任者による完了操作で、次回以降のシートには引き継がれません。
              </p>
              <div class="review-table">
                <table>
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th class="previous-action-sheet-number">回</th>
                      <th>ネクストアクション</th>
                      <th class="previous-action-result">結果</th>
                      @if (viewer.canManage) {
                        <th class="previous-action-operation">操作</th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of previousNextActionRows; track row.key) {
                      <tr>
                        <td>{{ row.number }}</td>
                        <td class="previous-action-sheet-number">
                          第{{ row.sheetNumber }}回
                        </td>
                        <td>{{ row.text }}</td>
                        <td class="previous-action-result">
                          @if (
                            canEditPreviousResults &&
                            previousResultForms.has(row.nextActionId)
                          ) {
                            @let form = previousResultForm(row.nextActionId);
                            <fieldset
                              class="previous-result-input"
                              [formGroup]="form"
                            >
                              <legend class="sr-only">
                                第{{ row.sheetNumber }}回 ネクストアクション{{
                                  row.number
                                }}の結果
                              </legend>
                              @for (
                                choice of nextActionResultChoices;
                                track choice.value
                              ) {
                                <label class="previous-result-choice">
                                  <input
                                    type="radio"
                                    [formControl]="form.controls.choice"
                                    [name]="
                                      'previous-result-' + row.nextActionId
                                    "
                                    [attr.name]="
                                      'previous-result-' + row.nextActionId
                                    "
                                    [value]="choice.value"
                                    (click)="
                                      onPreviousResultChoiceChanged(
                                        row.nextActionId,
                                        choice.value
                                      )
                                    "
                                  />
                                  <span>{{ choice.label }}</span>
                                </label>
                              }
                              @if (isOtherResultChoice(row.nextActionId)) {
                                <textarea
                                  cdkTextareaAutosize
                                  cdkAutosizeMinRows="2"
                                  cdkAutosizeMaxRows="20"
                                  class="feedback-textarea"
                                  formControlName="note"
                                  rows="2"
                                  maxlength="2000"
                                  placeholder="その他の結果を入力してください"
                                  [attr.aria-label]="
                                    '第' +
                                    row.sheetNumber +
                                    '回 ネクストアクション' +
                                    row.number +
                                    'の結果（その他）'
                                  "
                                  (blur)="
                                    savePreviousNextActionResult(
                                      row.nextActionId
                                    )
                                  "
                                ></textarea>
                                @if (form.controls.note.value.trim() === '') {
                                  <span class="result-unsaved-hint">
                                    @if (!row.hasPersistedResult) {
                                      その他の内容を入力すると保存されます
                                    } @else {
                                      保存済みの内容（{{
                                        row.persistedResultLabel
                                      }}）は変更されていません。新しい内容を入力すると上書きされます。このまま記入完了すると保存済みの内容が残ります。
                                    }
                                  </span>
                                }
                              }
                            </fieldset>
                            @if (row.isClosed) {
                              <span class="result-badge result-closed"
                                >完了</span
                              >
                            } @else if (row.resultLabel == null) {
                              <span class="result-waiting">未回答</span>
                            }
                          } @else {
                            @if (!row.hasPersistedResult && !row.isClosed) {
                              <span class="result-waiting"> 未回答 </span>
                            } @else {
                              <div class="result-display">
                                <span
                                  class="result-badge"
                                  [class.result-closed]="
                                    row.resultClass === 'closed'
                                  "
                                  [class.result-success]="
                                    row.resultClass === 'success'
                                  "
                                  [class.result-warning]="
                                    row.resultClass === 'warning'
                                  "
                                  [class.result-error]="
                                    row.resultClass === 'error'
                                  "
                                >
                                  {{ row.resultLabel }}
                                </span>
                                @if (
                                  row.isClosed && row.resultClass !== 'closed'
                                ) {
                                  <span class="result-badge result-closed"
                                    >完了</span
                                  >
                                }
                                @if (row.resultNote != null) {
                                  <span class="result-note">
                                    {{ row.resultNote }}
                                  </span>
                                }
                              </div>
                            }
                          }
                        </td>
                        @if (viewer.canManage) {
                          <td class="previous-action-operation">
                            <button
                              type="button"
                              class="btn btn-ghost btn-sm"
                              [attr.aria-label]="
                                row.text +
                                (row.isClosed ? 'を再開' : 'を完了にする')
                              "
                              [disabled]="
                                isPreviousNextActionUpdating(row) ||
                                isChangingAssessments
                              "
                              [attr.aria-busy]="
                                isPreviousNextActionUpdating(row)
                                  ? 'true'
                                  : null
                              "
                              (click)="
                                setPreviousNextActionClosed(row, !row.isClosed)
                              "
                            >
                              {{
                                isPreviousNextActionUpdating(row)
                                  ? row.isClosed
                                    ? '再開中...'
                                    : '完了処理中...'
                                  : row.isClosed
                                    ? '再開'
                                    : '完了'
                              }}
                            </button>
                          </td>
                        }
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </section>
          }

          <section class="form-section">
            <div class="form-section-title">
              <span class="section-number">1</span>
              <h3>実施情報</h3>
            </div>
            <div class="form-grid">
              <div class="form-field" [class.read-only]="!viewer.canManage">
                <div class="field-head">
                  @if (viewer.canManage) {
                    <label
                      class="field-label"
                      id="feedback-interview-date-label"
                      for="feedback-interview-date"
                    >
                      実施日
                    </label>
                  } @else {
                    <span
                      class="field-label"
                      id="feedback-interview-date-label"
                    >
                      実施日
                    </span>
                  }
                  <span class="writer-label writer-interview">面談中</span>
                  @if (!viewer.canManage) {
                    <ng-container *ngTemplateOutlet="readOnlyBadge" />
                  }
                </div>
                @if (viewer.canManage) {
                  <input
                    id="feedback-interview-date"
                    class="feedback-input"
                    type="date"
                    formControlName="interviewDate"
                    [attr.aria-label]="
                      '第' + selectedSheet.sheetNumber + '回の実施日'
                    "
                    (blur)="saveFeedbackSheetField('interviewDate')"
                  />
                } @else {
                  <div
                    class="read-only-value"
                    role="group"
                    id="feedback-interview-date"
                    aria-labelledby="feedback-interview-date-label"
                  >
                    @if (sheetForm.controls.interviewDate.value !== '') {
                      {{
                        sheetForm.controls.interviewDate.value
                          | date: 'yyyy/MM/dd'
                      }}
                    } @else {
                      <span class="read-only-empty">未記入</span>
                    }
                  </div>
                }
              </div>
              <div class="form-field">
                <div class="field-head">
                  <label class="field-label" for="feedback-consultation-topics">
                    相談事項
                  </label>
                  <span class="writer-label writer-operator">
                    オペレーター
                  </span>
                </div>
                <textarea
                  cdkTextareaAutosize
                  cdkAutosizeMinRows="3"
                  cdkAutosizeMaxRows="20"
                  id="feedback-consultation-topics"
                  class="feedback-textarea"
                  formControlName="consultationTopics"
                  (blur)="saveFeedbackSheetField('consultationTopics')"
                ></textarea>
              </div>
              <div class="form-field">
                <div class="field-head">
                  <label
                    class="field-label"
                    for="feedback-operator-self-issues"
                  >
                    オペレーター自身の課題感
                  </label>
                  <span class="writer-label writer-operator">
                    オペレーター
                  </span>
                </div>
                <textarea
                  cdkTextareaAutosize
                  cdkAutosizeMinRows="4"
                  cdkAutosizeMaxRows="20"
                  id="feedback-operator-self-issues"
                  class="feedback-textarea tall"
                  formControlName="operatorSelfIssues"
                  (blur)="saveFeedbackSheetField('operatorSelfIssues')"
                ></textarea>
              </div>
              <div class="form-field" [class.read-only]="!viewer.canManage">
                <div class="field-head">
                  @if (viewer.canManage) {
                    <label
                      class="field-label"
                      id="feedback-manager-hypothesis-label"
                      for="feedback-manager-hypothesis"
                    >
                      主任側の課題仮説
                    </label>
                  } @else {
                    <span
                      class="field-label"
                      id="feedback-manager-hypothesis-label"
                    >
                      主任側の課題仮説
                    </span>
                  }
                  <span class="writer-label writer-manager">主任</span>
                  @if (!viewer.canManage) {
                    <ng-container *ngTemplateOutlet="readOnlyBadge" />
                  }
                </div>
                <div
                  class="field-editor"
                  [class.generating-wrap]="
                    isAiDraftGenerating &&
                    sheetForm.controls.managerHypothesis.value === ''
                  "
                >
                  @if (
                    isAiDraftGenerating &&
                    sheetForm.controls.managerHypothesis.value === ''
                  ) {
                    <span class="spinner" aria-hidden="true"></span>
                    <span class="generating-label">
                      AI が草案を作成しています…
                    </span>
                  }
                  @if (viewer.canManage) {
                    <textarea
                      cdkTextareaAutosize
                      cdkAutosizeMinRows="4"
                      cdkAutosizeMaxRows="20"
                      id="feedback-manager-hypothesis"
                      class="feedback-textarea tall"
                      formControlName="managerHypothesis"
                      (focus)="
                        setAiDraftFieldEditing(null, 'managerHypothesis', true)
                      "
                      (blur)="
                        setAiDraftFieldEditing(
                          null,
                          'managerHypothesis',
                          false
                        );
                        saveFeedbackSheetField('managerHypothesis')
                      "
                    ></textarea>
                  } @else {
                    <div
                      class="read-only-value"
                      role="group"
                      id="feedback-manager-hypothesis"
                      aria-labelledby="feedback-manager-hypothesis-label"
                    >
                      @if (sheetForm.controls.managerHypothesis.value !== '') {
                        {{ sheetForm.controls.managerHypothesis.value }}
                      } @else if (!isAiDraftGenerating) {
                        <span class="read-only-empty">未記入</span>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          </section>

          <section class="form-section assessment-section">
            <div class="form-section-title">
              <span class="section-number">2</span>
              <h3>アセスメントへのフィードバック</h3>
            </div>

            @for (
              assessmentViewModel of feedbackAssessmentViewModels;
              track assessmentViewModel.assessment.sheetAssessmentId
            ) {
              @let assessment = assessmentViewModel.assessment;
              <article
                class="assessment-block"
                [formGroup]="assessmentViewModel.form"
              >
                <div class="assessment-head">
                  <div>
                    <div class="assessment-order">
                      <span>{{ $index + 1 }}</span>
                      <h4>
                        {{
                          assessmentViewModel.assessDateText
                        }}｜対象アセスメント
                      </h4>
                    </div>
                    <p class="meeting-title">
                      {{ assessmentViewModel.meetingTitleText }}
                    </p>
                    <a
                      class="assessment-link"
                      [routerLink]="['/home']"
                      [queryParams]="assessmentViewModel.assessmentLinkParams"
                      target="_blank"
                      rel="noopener noreferrer"
                      [attr.aria-label]="
                        assessmentViewModel.assessmentLinkLabel
                      "
                    >
                      <mat-icon aria-hidden="true">open_in_new</mat-icon>
                      アセスメントを開く
                    </a>
                  </div>
                  <div class="score-summary">
                    <div class="score-pill">
                      <span>総合スコア</span>
                      <strong>{{
                        assessmentViewModel.overallScoreText
                      }}</strong>
                    </div>
                    <div class="score-pill">
                      <span>重点KA平均</span>
                      <strong>
                        {{ assessmentViewModel.keyActionAverageText }}
                      </strong>
                    </div>
                  </div>
                </div>

                @if (assessment.keyActionScores.length > 0) {
                  <div
                    class="ka-score-strip"
                    aria-label="対象アセスメントの重点KeyActionスコア"
                  >
                    @for (
                      score of assessmentViewModel.keyActionScoreRows;
                      track score.keyActionId
                    ) {
                      <div class="ka-score-item">
                        <span>重点KeyAction</span>
                        <strong>
                          <span class="key-action-name">
                            {{ score.keyActionName }}
                          </span>
                          <b class="ka-score-value"> {{ score.score }} / 5 </b>
                        </strong>
                      </div>
                    }
                  </div>
                }

                <div class="recognition-grid">
                  <div
                    class="form-field"
                    data-testid="operator-good-points-field"
                  >
                    <div class="field-head">
                      <span class="field-label">
                        オペレーター自身が思う良かった点
                      </span>
                      <span class="writer-label writer-operator">
                        オペレーター
                      </span>
                    </div>
                    <fieldset class="operator-feedback-editor">
                      <legend class="sr-only">
                        オペレーター自身が思う良かった点
                      </legend>
                      <div class="feedback-choice-grid">
                        @for (
                          choice of operatorGoodPointChoices;
                          track choice.id;
                          let choiceIndex = $index
                        ) {
                          <label class="feedback-check-option">
                            <input
                              type="checkbox"
                              [formControl]="
                                assessmentViewModel.form.controls.operatorGoodPoints.controls.choices.at(
                                  choiceIndex
                                ).controls.selected
                              "
                              (change)="
                                saveFeedbackAssessmentField(
                                  assessment.sheetAssessmentId,
                                  'operatorGoodPoints'
                                )
                              "
                            />
                            <span>{{ choice.label }}</span>
                          </label>
                        }
                      </div>
                      <label
                        class="operator-feedback-free-label"
                        [for]="
                          'feedback-operator-good-points-' +
                          assessment.sheetAssessmentId
                        "
                      >
                        その他
                      </label>
                      <textarea
                        cdkTextareaAutosize
                        cdkAutosizeMinRows="3"
                        cdkAutosizeMaxRows="20"
                        class="feedback-textarea"
                        [id]="
                          'feedback-operator-good-points-' +
                          assessment.sheetAssessmentId
                        "
                        [formControl]="
                          assessmentViewModel.form.controls.operatorGoodPoints
                            .controls.freeText
                        "
                        (blur)="
                          saveFeedbackAssessmentField(
                            assessment.sheetAssessmentId,
                            'operatorGoodPoints'
                          )
                        "
                      ></textarea>
                    </fieldset>
                  </div>

                  <div
                    class="form-field"
                    data-testid="operator-improvement-points-field"
                  >
                    <div class="field-head">
                      <span class="field-label">
                        オペレーター自身が思う改善点
                      </span>
                      <span class="writer-label writer-operator">
                        オペレーター
                      </span>
                    </div>
                    <fieldset class="operator-feedback-editor">
                      <legend class="sr-only">
                        オペレーター自身が思う改善点
                      </legend>
                      <div class="feedback-choice-grid">
                        @for (
                          choice of operatorImprovementPointChoices;
                          track choice.id;
                          let choiceIndex = $index
                        ) {
                          <label class="feedback-check-option">
                            <input
                              type="checkbox"
                              [formControl]="
                                assessmentViewModel.form.controls.operatorImprovementPoints.controls.choices.at(
                                  choiceIndex
                                ).controls.selected
                              "
                              (change)="
                                saveFeedbackAssessmentField(
                                  assessment.sheetAssessmentId,
                                  'operatorImprovementPoints'
                                )
                              "
                            />
                            <span>{{ choice.label }}</span>
                          </label>
                        }
                      </div>
                      <label
                        class="operator-feedback-free-label"
                        [for]="
                          'feedback-operator-improvement-points-' +
                          assessment.sheetAssessmentId
                        "
                      >
                        その他
                      </label>
                      <textarea
                        cdkTextareaAutosize
                        cdkAutosizeMinRows="3"
                        cdkAutosizeMaxRows="20"
                        class="feedback-textarea"
                        [id]="
                          'feedback-operator-improvement-points-' +
                          assessment.sheetAssessmentId
                        "
                        [formControl]="
                          assessmentViewModel.form.controls
                            .operatorImprovementPoints.controls.freeText
                        "
                        (blur)="
                          saveFeedbackAssessmentField(
                            assessment.sheetAssessmentId,
                            'operatorImprovementPoints'
                          )
                        "
                      ></textarea>
                    </fieldset>
                  </div>

                  <div class="form-field" [class.read-only]="!viewer.canManage">
                    <div class="field-head">
                      @if (viewer.canManage) {
                        <label
                          class="field-label"
                          [id]="
                            'feedback-good-points-label-' +
                            assessment.sheetAssessmentId
                          "
                          [attr.for]="
                            'feedback-good-points-' +
                            assessment.sheetAssessmentId
                          "
                        >
                          良かった点
                        </label>
                      } @else {
                        <span
                          class="field-label"
                          [id]="
                            'feedback-good-points-label-' +
                            assessment.sheetAssessmentId
                          "
                        >
                          良かった点
                        </span>
                      }
                      <span class="writer-label writer-manager">主任</span>
                      @if (!viewer.canManage) {
                        <ng-container *ngTemplateOutlet="readOnlyBadge" />
                      }
                    </div>
                    <div
                      class="field-editor"
                      [class.generating-wrap]="
                        isAiDraftGenerating &&
                        assessmentViewModel.form.controls.goodPoints.value ===
                          ''
                      "
                    >
                      @if (
                        isAiDraftGenerating &&
                        assessmentViewModel.form.controls.goodPoints.value ===
                          ''
                      ) {
                        <span class="spinner" aria-hidden="true"></span>
                        <span class="generating-label">
                          AI が草案を作成しています…
                        </span>
                      }
                      @if (viewer.canManage) {
                        <textarea
                          cdkTextareaAutosize
                          cdkAutosizeMinRows="4"
                          cdkAutosizeMaxRows="20"
                          [id]="
                            'feedback-good-points-' +
                            assessment.sheetAssessmentId
                          "
                          class="feedback-textarea tall"
                          formControlName="goodPoints"
                          (focus)="
                            setAiDraftFieldEditing(
                              assessment.sheetAssessmentId,
                              'goodPoints',
                              true
                            )
                          "
                          (blur)="
                            setAiDraftFieldEditing(
                              assessment.sheetAssessmentId,
                              'goodPoints',
                              false
                            );
                            saveFeedbackAssessmentField(
                              assessment.sheetAssessmentId,
                              'goodPoints'
                            )
                          "
                        ></textarea>
                      } @else {
                        <div
                          class="read-only-value"
                          role="group"
                          [id]="
                            'feedback-good-points-' +
                            assessment.sheetAssessmentId
                          "
                          [attr.aria-labelledby]="
                            'feedback-good-points-label-' +
                            assessment.sheetAssessmentId
                          "
                        >
                          @if (
                            assessmentViewModel.form.controls.goodPoints
                              .value !== ''
                          ) {
                            {{
                              assessmentViewModel.form.controls.goodPoints.value
                            }}
                          } @else if (!isAiDraftGenerating) {
                            <span class="read-only-empty">未記入</span>
                          }
                        </div>
                      }
                    </div>
                  </div>

                  <div class="form-field" [class.read-only]="!viewer.canManage">
                    <div class="field-head">
                      @if (viewer.canManage) {
                        <label
                          class="field-label"
                          [id]="
                            'feedback-improvement-points-label-' +
                            assessment.sheetAssessmentId
                          "
                          [attr.for]="
                            'feedback-improvement-points-' +
                            assessment.sheetAssessmentId
                          "
                        >
                          改善点
                        </label>
                      } @else {
                        <span
                          class="field-label"
                          [id]="
                            'feedback-improvement-points-label-' +
                            assessment.sheetAssessmentId
                          "
                        >
                          改善点
                        </span>
                      }
                      <span class="writer-label writer-manager">主任</span>
                      @if (!viewer.canManage) {
                        <ng-container *ngTemplateOutlet="readOnlyBadge" />
                      }
                    </div>
                    @if (viewer.canManage) {
                      <p
                        class="field-guide"
                        [id]="
                          'feedback-improvement-points-guide-' +
                          assessment.sheetAssessmentId
                        "
                      >
                        「会話のどの部分を」「どうしていたらもっと良かったか」「それはなぜか」の順に、できる限り行動ベースで具体的に書いてください。理由まで書くと、主観ではなく根拠のある指摘として伝わります。
                      </p>
                    }
                    <div
                      class="field-editor"
                      [class.generating-wrap]="
                        isAiDraftGenerating &&
                        assessmentViewModel.form.controls.improvementPoints
                          .value === ''
                      "
                    >
                      @if (
                        isAiDraftGenerating &&
                        assessmentViewModel.form.controls.improvementPoints
                          .value === ''
                      ) {
                        <span class="spinner" aria-hidden="true"></span>
                        <span class="generating-label">
                          AI が草案を作成しています…
                        </span>
                      }
                      @if (viewer.canManage) {
                        <textarea
                          cdkTextareaAutosize
                          cdkAutosizeMinRows="4"
                          cdkAutosizeMaxRows="20"
                          [id]="
                            'feedback-improvement-points-' +
                            assessment.sheetAssessmentId
                          "
                          class="feedback-textarea tall"
                          formControlName="improvementPoints"
                          [attr.aria-describedby]="
                            'feedback-improvement-points-guide-' +
                            assessment.sheetAssessmentId
                          "
                          (focus)="
                            setAiDraftFieldEditing(
                              assessment.sheetAssessmentId,
                              'improvementPoints',
                              true
                            )
                          "
                          (blur)="
                            setAiDraftFieldEditing(
                              assessment.sheetAssessmentId,
                              'improvementPoints',
                              false
                            );
                            saveFeedbackAssessmentField(
                              assessment.sheetAssessmentId,
                              'improvementPoints'
                            )
                          "
                        ></textarea>
                      } @else {
                        <div
                          class="read-only-value"
                          role="group"
                          [id]="
                            'feedback-improvement-points-' +
                            assessment.sheetAssessmentId
                          "
                          [attr.aria-labelledby]="
                            'feedback-improvement-points-label-' +
                            assessment.sheetAssessmentId
                          "
                        >
                          @if (
                            assessmentViewModel.form.controls.improvementPoints
                              .value !== ''
                          ) {
                            {{
                              assessmentViewModel.form.controls
                                .improvementPoints.value
                            }}
                          } @else if (!isAiDraftGenerating) {
                            <span class="read-only-empty">未記入</span>
                          }
                        </div>
                      }
                    </div>
                  </div>

                  <div
                    class="form-field next-actions-field"
                    [class.read-only]="!viewer.canManage"
                  >
                    <div class="field-head">
                      <span
                        class="field-label"
                        [id]="
                          'feedback-next-actions-label-' +
                          assessment.sheetAssessmentId
                        "
                        >ネクストアクション</span
                      >
                      <span class="writer-label writer-interview">
                        面談中
                      </span>
                      @if (!viewer.canManage) {
                        <ng-container *ngTemplateOutlet="readOnlyBadge" />
                      }
                    </div>
                    <div
                      class="field-editor"
                      [class.generating-wrap]="
                        isAiDraftGenerating &&
                        assessmentViewModel.form.controls.nextActions.length ===
                          0
                      "
                    >
                      @if (
                        isAiDraftGenerating &&
                        assessmentViewModel.form.controls.nextActions.length ===
                          0
                      ) {
                        <span class="spinner" aria-hidden="true"></span>
                        <span class="generating-label">
                          AI が草案を作成しています…
                        </span>
                      }
                      @if (
                        !viewer.canManage &&
                        !isAiDraftGenerating &&
                        assessmentViewModel.form.controls.nextActions.length ===
                          0
                      ) {
                        <div class="waiting-state">
                          <mat-icon aria-hidden="true">info_outline</mat-icon>
                          <span
                            >ネクストアクションはまだ登録されていません</span
                          >
                        </div>
                      }
                      @if (viewer.canManage) {
                        <div class="action-list" formArrayName="nextActions">
                          @for (
                            actionForm of assessmentViewModel.form.controls
                              .nextActions.controls;
                            track actionForm.controls.id.value
                          ) {
                            @let actionId = actionForm.controls.id.value;
                            <div class="action-row" [formGroupName]="$index">
                              <span class="action-number">
                                {{ $index + 1 }}
                              </span>
                              <div class="action-input-wrap">
                                <input
                                  class="feedback-input"
                                  type="text"
                                  formControlName="text"
                                  placeholder="実行する行動を1件ずつ入力してください"
                                  [attr.aria-label]="
                                    'ネクストアクション ' + ($index + 1)
                                  "
                                  (focus)="
                                    setAiDraftFieldEditing(
                                      assessment.sheetAssessmentId,
                                      'nextActions',
                                      true
                                    )
                                  "
                                  (blur)="
                                    setAiDraftFieldEditing(
                                      assessment.sheetAssessmentId,
                                      'nextActions',
                                      false
                                    );
                                    saveNextActions(
                                      assessment.sheetAssessmentId
                                    )
                                  "
                                />
                                @if (
                                  assessmentViewModel.nextActionResultTexts[
                                    actionId
                                  ];
                                  as resultText
                                ) {
                                  <span class="action-result">
                                    {{ resultText }}
                                  </span>
                                }
                              </div>
                              <button
                                class="remove-action"
                                type="button"
                                [disabled]="isChangingAssessments"
                                [attr.aria-label]="
                                  'ネクストアクション' + ($index + 1) + 'を削除'
                                "
                                (click)="
                                  removeNextAction(
                                    assessment.sheetAssessmentId,
                                    $index
                                  )
                                "
                              >
                                <mat-icon aria-hidden="true">delete</mat-icon>
                              </button>
                            </div>
                          }
                        </div>
                      } @else {
                        @if (
                          assessmentViewModel.form.controls.nextActions.length >
                          0
                        ) {
                          <ol
                            class="read-only-action-list"
                            [attr.aria-labelledby]="
                              'feedback-next-actions-label-' +
                              assessment.sheetAssessmentId
                            "
                          >
                            @for (
                              actionForm of assessmentViewModel.form.controls
                                .nextActions.controls;
                              track actionForm.controls.id.value
                            ) {
                              @let actionId = actionForm.controls.id.value;
                              <li>
                                <span>{{
                                  actionForm.controls.text.value
                                }}</span>
                                @if (
                                  assessmentViewModel.nextActionResultTexts[
                                    actionId
                                  ];
                                  as resultText
                                ) {
                                  <span class="action-result">{{
                                    resultText
                                  }}</span>
                                }
                              </li>
                            }
                          </ol>
                        }
                      }
                    </div>
                    @if (viewer.canManage) {
                      <button
                        class="btn btn-ghost btn-sm add-action"
                        type="button"
                        [disabled]="
                          isNextActionAddBlocked || isChangingAssessments
                        "
                        [attr.title]="
                          isNextActionAddBlocked
                            ? 'AI 草案の生成中は追加できません'
                            : null
                        "
                        [attr.aria-label]="
                          isNextActionAddBlocked
                            ? 'ネクストアクションを追加（AI 草案の生成中は追加できません）'
                            : 'ネクストアクションを追加'
                        "
                        (click)="addNextAction(assessment.sheetAssessmentId)"
                      >
                        + ネクストアクションを追加
                      </button>
                    }
                  </div>
                </div>
              </article>
            }
          </section>

          <section class="form-section">
            <div class="form-section-title">
              <span class="section-number">3</span>
              <h3>その他</h3>
            </div>
            <div class="form-grid">
              <div class="form-field" [class.read-only]="!viewer.canManage">
                <div class="field-head">
                  @if (viewer.canManage) {
                    <label
                      class="field-label"
                      id="feedback-other-notes-label"
                      for="feedback-other-notes"
                    >
                      特記事項
                    </label>
                  } @else {
                    <span class="field-label" id="feedback-other-notes-label">
                      特記事項
                    </span>
                  }
                  <span class="writer-label writer-interview">面談中</span>
                  @if (!viewer.canManage) {
                    <ng-container *ngTemplateOutlet="readOnlyBadge" />
                  }
                </div>
                <div
                  class="field-editor"
                  [class.generating-wrap]="
                    isAiDraftGenerating &&
                    sheetForm.controls.otherNotes.value === ''
                  "
                >
                  @if (
                    isAiDraftGenerating &&
                    sheetForm.controls.otherNotes.value === ''
                  ) {
                    <span class="spinner" aria-hidden="true"></span>
                    <span class="generating-label">
                      AI が草案を作成しています…
                    </span>
                  }
                  @if (viewer.canManage) {
                    <textarea
                      cdkTextareaAutosize
                      cdkAutosizeMinRows="3"
                      cdkAutosizeMaxRows="20"
                      id="feedback-other-notes"
                      class="feedback-textarea"
                      formControlName="otherNotes"
                      (focus)="setAiDraftFieldEditing(null, 'otherNotes', true)"
                      (blur)="
                        setAiDraftFieldEditing(null, 'otherNotes', false);
                        saveFeedbackSheetField('otherNotes')
                      "
                    ></textarea>
                  } @else {
                    <div
                      class="read-only-value"
                      role="group"
                      id="feedback-other-notes"
                      aria-labelledby="feedback-other-notes-label"
                    >
                      @if (sheetForm.controls.otherNotes.value !== '') {
                        {{ sheetForm.controls.otherNotes.value }}
                      } @else if (!isAiDraftGenerating) {
                        <span class="read-only-empty">未記入</span>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    }
  }
</section>
